// Third-party
import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { camelCase } from 'lodash';
import classNames from 'classnames';

// Namespaces
import * as ProposalNS from '../../Proposal';

// Components
import { ContentPanelDragPreviewItem } from '@/views/components/misc/drag-and-drop/ContentPanelDragPreview';
import { IconButton } from '@/views/components/buttons';
import { Tooltip } from '@/views/components/tooltips';

// Utils
import { formatMessage } from '@/utils/i18n';
import { DragItemTypes, DropItemPosition } from '@/utils/drag-and-drop';

type Props = {
  element: ProposalNS.ProposalTemplateSectionElement;
  label: string;
  disabled?: boolean;
  locked?: boolean;
  onChangeContentOrder: (item: string, reference: string, position?: DropItemPosition) => void;
  onRemoveOption: (option: ProposalNS.ProposalTemplateDivisionElement | ProposalNS.ProposalTemplateSectionElement) => void;
  onJumpToPage: (section: string) => void;
};

export default function ContentPanelOption(props: Props) {
  const { element, label, disabled, locked } = props;
  const [dropPosition, setDropPosition] = useState<DropItemPosition | null>(null);
  const itemWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  });

  const getMonitorDropPosition = (monitor: DropTargetMonitor) => {
    const clientOffset = monitor.getClientOffset();
    if (!clientOffset || !itemWrapperRef.current) {
      return null;
    }

    const clientRect = itemWrapperRef.current.getBoundingClientRect();

    if (clientOffset.y < clientRect.y + clientRect.height / 2) {
      return DropItemPosition.Before;
    }

    return DropItemPosition.After;
  };

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: DragItemTypes.ProposalContentItemOption,
      item: {
        key: element?.key,
        label,
        disabled,
      } as ContentPanelDragPreviewItem,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [element, disabled, dropPosition],
  );

  const [{ isActive: isDropping }, drop] = useDrop(
    () => ({
      accept: DragItemTypes.ProposalContentItemOption,
      drop: (droppedItem: ContentPanelDragPreviewItem) => {
        if (!dropPosition || disabled) {
          return;
        }

        props.onChangeContentOrder(droppedItem.key, element.key, dropPosition);
      },
      hover: (dragItem, monitor) => {
        setDropPosition(getMonitorDropPosition(monitor));
      },
      collect: monitor => ({
        isActive: monitor.canDrop() && monitor.isOver() && monitor.getItem() !== null && monitor.getItem().key !== element.key,
      }),
    }),
    [dropPosition, element],
  );

  if (!label) {
    return null;
  }

  return (
    <div ref={!locked ? itemWrapperRef : undefined} className="content-panel-option">
      <div
        ref={!locked ? drop : undefined}
        key={`content-panel-option-item-drop-${element.key}`}
        className={classNames(`content-panel-option-item-drop ${element.key}`, {
          'is-locked': locked,
          'is-disabled': disabled,
        })}
      >
        <div
          ref={!locked ? drag : undefined}
          data-private
          data-dd-privacy="mask"
          className={classNames('content-panel-option-item', {
            disabled,
            dragging: isDragging,
            [`dropping-${dropPosition?.toLowerCase()}`]: isDropping,
          })}
        >
          <div className="drag-handle">
            <i className="icon-drag-vertical" />
          </div>
          <span>
            {label}

            {locked && (
              <Tooltip message={formatMessage('Proposal.LockedPage')}>
                <i className="icon-lock-outline" />
              </Tooltip>
            )}

            <div className="actions">
              <IconButton
                ariaLabel={`${formatMessage('Proposal.JumpToPage')}: ${label}`}
                icon="jump-to"
                tooltip={formatMessage('Proposal.JumpToPage')}
                onClick={() => props.onJumpToPage(element.key)}
              />
              {!disabled && (
                <IconButton
                  ariaLabel={`${formatMessage('Proposal.DeletePage')}: ${label}`}
                  icon="ds-delete"
                  tooltip={formatMessage('Proposal.DeletePage')}
                  onClick={() => props.onRemoveOption(element)}
                />
              )}
            </div>
          </span>
        </div>
      </div>
    </div>
  );
}
